import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import type { Dispatch, ReactElement } from 'react';
import { Controller, useForm, type Control, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Routes } from '../../../routes/routes';
import { useAuthStore } from '../../../stores';
import { PasswordField } from '../_components/PasswordField';
import { StepHeading } from '../_components/StepHeading';
import { mapLoginError } from '../loginErrorMap';
import type { LoginAction, LoginState } from '../loginStateMachine';

/**
 * `LoginStep` — single-step email + password form, replaces the legacy
 * PhoneStep + OtpStep duo (AUT-110).
 *
 * Submit flow:
 *   1. Zod-validate {email, password} client-side (anti-empty + email shape).
 *   2. Call `authStore.loginWithPassword(email, password)` — store handles
 *      the tagged-union response internally.
 *   3. Branch on the resolved `kind`:
 *      - `tokens` → navigate to Dashboard (login complete).
 *      - `totp_required` → dispatch `login_requires_totp` (advance state
 *        machine to the TOTP step; tempToken stashed).
 *      - `totp_enrollment_required` → navigate to Dashboard (session is
 *        live; enrolment UX deferred — see AuthStore note).
 *   4. On failure: map via `loginErrorMap`, dispatch `submit_failed`.
 *
 * The LoginStep does NOT preview the password policy — the user is
 * entering an existing password, the server is the source of truth for
 * "is this credential pair valid". Policy-preview UI lives in
 * `SetPasswordPage` + `ResetConfirmPage`.
 */

const LoginSchema = z.object({
  email: z.string().min(1, 'auth.login.email.error').email('auth.login.email.error'),
  password: z.string().min(1, 'auth.login.password.required'),
});

type LoginFormValues = z.infer<typeof LoginSchema>;

export type LoginStepProps = {
  state: LoginState;
  dispatch: Dispatch<LoginAction>;
};

type FieldsProps = {
  control: Control<LoginFormValues>;
  disabled: boolean;
  emailLabel: string;
  emailPlaceholder: string;
  passwordLabel: string;
  passwordPlaceholder: string;
};

/**
 * Render the email + password controllers. Extracted out of LoginStep's
 * render so the parent stays under the 80-line max-lines-per-function
 * lint cap.
 */
const LoginFields = ({
  control,
  disabled,
  emailLabel,
  emailPlaceholder,
  passwordLabel,
  passwordPlaceholder,
}: FieldsProps): ReactElement => (
  <>
    <Controller
      name="email"
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          fullWidth
          type="email"
          autoComplete="username"
          label={emailLabel}
          placeholder={emailPlaceholder}
          disabled={disabled}
          error={Boolean(fieldState.error)}
          inputProps={{ 'aria-label': emailLabel, 'data-testid': 'login-email-input' }}
          sx={{ mb: '16px' }}
        />
      )}
    />
    <Controller
      name="password"
      control={control}
      render={({ field, fieldState }) => (
        <PasswordField
          {...field}
          fullWidth
          autoComplete="current-password"
          label={passwordLabel}
          placeholder={passwordPlaceholder}
          disabled={disabled}
          error={Boolean(fieldState.error)}
          inputProps={{ 'aria-label': passwordLabel, 'data-testid': 'login-password-input' }}
          sx={{ mb: '16px' }}
        />
      )}
    />
  </>
);

type AlertRowProps = {
  errorKey: string | null;
  errors: FieldErrors<LoginFormValues>;
  emailErrorLabel: string;
  t: (key: string) => string;
};

const AlertRow = ({ errorKey, errors, emailErrorLabel, t }: AlertRowProps): ReactElement | null => {
  if (errorKey) {
    return (
      <Alert severity="error" sx={{ mb: '16px' }}>
        {t(errorKey)}
      </Alert>
    );
  }
  if (errors.email) {
    return (
      <Alert severity="warning" sx={{ mb: '16px' }}>
        {emailErrorLabel}
      </Alert>
    );
  }
  return null;
};

type SubmitFooterProps = {
  ctaLabel: string;
  forgotLabel: string;
  disabled: boolean;
};

const SubmitFooter = ({ ctaLabel, forgotLabel, disabled }: SubmitFooterProps): ReactElement => (
  <>
    <Button
      type="submit"
      fullWidth
      variant="contained"
      size="large"
      disabled={disabled}
      sx={{ height: 52, borderRadius: '14px' }}
      data-testid="login-submit"
    >
      {ctaLabel}
    </Button>
    <Box sx={{ mt: '16px', textAlign: 'center' }}>
      <Link
        component={RouterLink}
        to={Routes.Auth.ResetRequest}
        underline="hover"
        sx={{ fontSize: 13 }}
      >
        {forgotLabel}
      </Link>
    </Box>
  </>
);

export const LoginStep = ({ state, dispatch }: LoginStepProps): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const authStore = useAuthStore();
  const resetSuccess = Boolean((location.state as { resetSuccess?: boolean } | null)?.resetSuccess);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: state.email, password: '' },
    mode: 'onSubmit',
  });

  const onSubmit = async (values: LoginFormValues): Promise<void> => {
    dispatch({ type: 'submit_started' });
    try {
      const outcome = await authStore.loginWithPassword(values.email, values.password);
      if (outcome.kind === 'tokens' || outcome.kind === 'totp_enrollment_required') {
        dispatch({ type: 'login_complete', email: values.email });
        navigate(Routes.App.Dashboard, { replace: true });
        return;
      }
      dispatch({
        type: 'login_requires_totp',
        email: values.email,
        tempToken: outcome.tempToken,
      });
    } catch (err) {
      const mapped = mapLoginError(err, 'login');
      dispatch({ type: 'submit_failed', errorKey: mapped.key });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <StepHeading
        eyebrow={t('auth.login.step.login')}
        title={t('auth.login.title')}
        subtitle={t('auth.login.subtitle')}
      />
      <LoginFields
        control={control}
        disabled={state.isSubmitting}
        emailLabel={t('auth.login.email.label')}
        emailPlaceholder={t('auth.login.email.placeholder')}
        passwordLabel={t('auth.login.password.label')}
        passwordPlaceholder={t('auth.login.password.placeholder')}
      />
      {resetSuccess && !state.errorKey && (
        <Alert severity="success" sx={{ mb: '16px' }} data-testid="reset-success">
          {t('auth.reset.confirm.successMessage')}
        </Alert>
      )}
      <AlertRow
        errorKey={state.errorKey}
        errors={errors}
        emailErrorLabel={t('auth.login.email.error')}
        t={t}
      />
      <SubmitFooter
        ctaLabel={t('auth.login.cta')}
        forgotLabel={t('auth.login.password.forgotLink')}
        disabled={state.isSubmitting}
      />
    </Box>
  );
};
